import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Header } from '@/components/layout/header/Header';
import { MasterClassBanner } from '@/components/masterclass/MasterClassBanner';
import { MasterClassGrid } from '@/components/masterclass/MasterClassGrid';
import { MasterClassEmpty } from '@/components/masterclass/MasterClassEmpty';
import { MasterClassError } from '@/components/masterclass/MasterClassError';
import { MasterClassFilters } from '@/components/masterclass/MasterClassFilters';
import { MasterClassFullScreen } from '@/components/masterclass/MasterClassFullScreen';
import type { Serie } from '@/types/masterclass';
import seriesData from '@/data/masterclass/series.json';

export default function MasterClass() {
  useLanguage();
  const [series, setSeries] = useState<Serie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  
  // Modal
  const [selectedSerie, setSelectedSerie] = useState<Serie | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Ordenação
  const [sortBy, setSortBy] = useState('progress');
  
  useEffect(() => {
    // Simular carregamento
    const timer = setTimeout(() => {
      try {
        setSeries(seriesData as Serie[]);
        setLoading(false);
      } catch (e) {
        console.error('Erro ao carregar séries:', e);
        setError(true);
        setLoading(false);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  // Handlers do modal
  const handleOpenSerie = (serie: Serie) => {
    setSelectedSerie(serie);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSerie(null);
  };

  // Filtrar séries
  const filteredSeries = useMemo(() => {
    let filtered = [...series];
    
    // Ordenação
    if (sortBy === 'recent') {
      filtered.reverse();
    }
    
    return filtered;
  }, [series, sortBy]);
  
  if (error) {
    return (
      <Layout>
        <MasterClassError onRetry={() => window.location.reload()} />
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header
          title={translateInterfaceText("Master Class")}
          subtitle={translateInterfaceText("Aulas exclusivas para acelerar sua evolução")}
        />
        
        <div className="hidden lg:block h-px bg-border w-full" />
        
        <main className="flex-1 overflow-y-auto">
          {/* Container */}
          <div className="container mx-auto px-4 md:px-6 py-8">
            {/* Banner */}
            <MasterClassBanner />
            
            {/* Scroll anchor */}
            <div id="modulos" className="scroll-mt-8 mt-8" />
            
            {/* Filtros */}
            {!loading && series.length > 0 && (
              <MasterClassFilters
                sortBy={sortBy}
                onSortChange={setSortBy}
              />
            )}
            
            {/* Grid de Cards */}
            {loading ? (
              <MasterClassGrid series={[]} loading />
            ) : filteredSeries.length === 0 ? (
              <MasterClassEmpty />
            ) : (
              <MasterClassGrid 
                series={filteredSeries} 
                loading={false}
                onOpenSerie={handleOpenSerie}
              />
            )}
          </div>
        </main>
      </div>

      {/* Modal */}
      {isModalOpen && selectedSerie && (
        <MasterClassFullScreen 
          serie={selectedSerie} 
          onClose={handleCloseModal} 
        />
      )}
    </Layout>
  );
}
